import { getSupabaseClient } from '../infrastructure/supabase.client.js';

/**
 * Servicio de autenticación de STAFF (panel /admin).
 *
 * Todo pasa por el cliente anon del navegador (supabase.client.js), por lo que
 * es 100% mockeable en tests. El acceso a datos lo enforcea RLS: un usuario solo
 * puede leer su propio perfil (policy profiles_select_own_or_staff).
 */

/** Roles con acceso al panel de staff. */
const STAFF_ROLES = new Set(['counter', 'admin']);

/**
 * ¿El rol dado corresponde a staff (counter/admin)? Helper puro.
 * @param {string|null|undefined} role
 * @returns {boolean}
 */
export function isStaff(role) {
    return STAFF_ROLES.has(role);
}

/**
 * Inicia sesión de staff con email + contraseña.
 * @param {{ email: string, password: string }} credentials
 * @returns {Promise<import('@supabase/supabase-js').AuthTokenResponsePassword['data']>}
 * @throws {Error} si Supabase no está configurado o las credenciales fallan.
 */
export async function signInStaff({ email, password }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Autenticación no disponible');

    const { data, error } = await client.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    return data;
}

/**
 * Cierra la sesión actual. No-op si Supabase no está configurado.
 * @returns {Promise<void>}
 */
export async function signOut() {
    const client = getSupabaseClient();
    if (!client) return;

    const { error } = await client.auth.signOut();
    if (error) throw new Error(error.message);
}

/**
 * Devuelve la sesión activa o null (sin sesión / sin configuración).
 * @returns {Promise<import('@supabase/supabase-js').Session | null>}
 */
export async function getCurrentSession() {
    const client = getSupabaseClient();
    if (!client) return null;

    const { data } = await client.auth.getSession();
    return data?.session ?? null;
}

/**
 * Suscribe un callback a los cambios de estado de auth (login/logout/refresh).
 * @param {(event: string, session: import('@supabase/supabase-js').Session | null) => void} cb
 * @returns {{ unsubscribe: () => void }} suscripción (no-op si no hay cliente).
 */
export function onAuthChange(cb) {
    const client = getSupabaseClient();
    if (!client) return { unsubscribe() {} };

    const { data } = client.auth.onAuthStateChange((event, session) => cb(event, session));
    return data.subscription;
}

/**
 * @typedef {{ ok: true, role: string | null }} StaffRoleOk
 * @typedef {{ ok: false, error: unknown }} StaffRoleErr
 * @typedef {StaffRoleOk | StaffRoleErr} StaffRoleResult
 */

/**
 * Lee el rol del usuario logueado desde la tabla profiles (vía RLS: cada usuario
 * puede leer su propio perfil).
 *
 * Devuelve un resultado discriminado para que el llamador distinga "no es staff"
 * de "no pudimos verificar": un fallo de red/DB NUNCA debe interpretarse como
 * "no es staff" (eso cerraría sesión a un staff legítimo).
 *  - `{ ok: true, role }` → consulta OK; `role` puede ser null/ausente.
 *  - `{ ok: false, error }` → la consulta falló o lanzó (transitorio, reintentar).
 * @returns {Promise<StaffRoleResult>}
 */
export async function getStaffRole() {
    const client = getSupabaseClient();
    if (!client) return { ok: true, role: null };

    try {
        const { data: sessionData } = await client.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) return { ok: true, role: null };

        const { data, error } = await client
            .from('profiles')
            .select('role')
            .eq('id', userId)
            .single();

        if (error) return { ok: false, error };
        return { ok: true, role: data?.role ?? null };
    } catch (error) {
        return { ok: false, error };
    }
}
